"use strict";

// The end-to-end workflow gives ingestion this long before it gives up, so a
// load not finished by then has stopped.
export const VIDEO_INGESTION_TIMEOUT_MS = 6 * 60 * 60 * 1000;

export const VIDEO_INGESTION_STATE = Object.freeze({
    loading: "loading",
    stalled: "stalled",
});

/**
 * Whether a new video is still loading into the project, or null when none is.
 *
 * The backend records videoIngestion { runId, requestedAt } when a run starts
 * loading a video; ingestion writes a videoUrl ending with that run id once
 * the video is ready.
 */
export const getVideoIngestionState = (project, now = Date.now()) => {
    const { runId, requestedAt } = project?.videoIngestion || {};
    const requestedMs = Date.parse(requestedAt);
    if (!runId || Number.isNaN(requestedMs)) {
        return null;
    }
    if (String(project?.videoUrl || "").includes(runId)) {
        return null;
    }
    return {
        state:
            now - requestedMs < VIDEO_INGESTION_TIMEOUT_MS
                ? VIDEO_INGESTION_STATE.loading
                : VIDEO_INGESTION_STATE.stalled,
        requestedAt: new Date(requestedMs),
    };
};

// The record a page keeps locally right after it requests a new video, until
// the project is fetched again.
export const pendingVideoIngestion = (runId, requestedAt = new Date()) =>
    runId ? { runId, requestedAt: requestedAt.toISOString() } : null;

export const formatRequestTime = (date, now = new Date()) =>
    date.toDateString() === now.toDateString()
        ? date.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })
        : date.toLocaleString([], {
            month: "short",
            day: "numeric",
            hour: "numeric",
            minute: "2-digit",
        });

// One sentence for the page, or null when no video is loading.
export const describeVideoIngestion = (ingestion, now = new Date()) => {
    if (!ingestion) {
        return null;
    }
    const time = formatRequestTime(ingestion.requestedAt, now);
    return ingestion.state === VIDEO_INGESTION_STATE.loading
        ? `A new video is loading (requested ${time}). It shows here once it is ready, which can take about half an hour for a feature.`
        : `The video requested ${time} did not finish loading. You can upload it again.`;
};
